<div class="sheet-buttons">
  <div class="tabs" *ngIf="sheets.length && sheets.length > 1">
    <h2>{{ "files_previewer.sheets" | translate }}</h2>
    <button *ngFor="let s of sheets; let i = index" [class.active]="i === selectedSheetIndex" (click)="setSelectedSheet(i)">
      {{ s.name }} <span *ngIf="s.hidden" class="hidden-flag">{{ "files_previewer.hidden" | translate }}</span>
    </button>
  </div>
  <div class="previewer-actions">
    <div class="zoom-controls" *ngIf="canZoom">
      <button
        type="button"
        class="zoom-btn"
        (click)="zoomOut()"
        [disabled]="!canZoomOut"
        [title]="'files_previewer.zoom_out' | translate"
        [attr.aria-label]="'files_previewer.zoom_out' | translate"
      >&minus;</button>
      <button
        type="button"
        class="zoom-reset"
        (click)="resetZoom()"
        [title]="'files_previewer.reset_zoom' | translate"
        [attr.aria-label]="'files_previewer.reset_zoom' | translate"
      >{{ zoomPercent }}%</button>
      <button
        type="button"
        class="zoom-btn"
        (click)="zoomIn()"
        [disabled]="!canZoomIn"
        [title]="'files_previewer.zoom_in' | translate"
        [attr.aria-label]="'files_previewer.zoom_in' | translate"
      >+</button>
    </div>
    <button class="export-button" (click)="export()" *ngIf="!isLoading && !isNotExport && !useIframe">
      <img src="assets/icones/export-icon.svg" alt="Download" />
    </button>
  </div>
</div>
<div class="files-previewer-container">
  <div class="export-button-container">
    <div *ngIf="isLoading" class="loading-container">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>{{ "files_previewer.loading_file" | translate }}</p>
      </div>
    </div>

    <div *ngIf="!isLoading && isExcelOrCsvFile">
      <!-- If cross-origin fetch is blocked by CORS, fall back to an iframe Office viewer -->
      <div *ngIf="useIframe" class="excel-iframe-container">
        <div *ngIf="originalFileUrl" style="margin-bottom: 12px;">
          <a [href]="originalFileUrl" target="_blank" rel="noopener noreferrer">Open / Download</a>
        </div>
        <iframe
          [src]="data[0]"
          style="width: 100%; height: 100vh; border: none;"
          title="Spreadsheet Viewer"
        ></iframe>
      </div>

      <div *ngIf="!useIframe && sheets.length">
        <div class="sheet-preview" *ngIf="getSelectedSheet() as sheet">
          <div class="grid-wrapper">
            <!-- WB-7592: two bindings per cell, deliberately — `hlLocked` rides on the table and
                 reverse hover is delegated on the <tbody>, to keep large sheets cheap. -->
            <table class="excel-table" aria-label="Excel sheet preview" [class.excel-table--locked]="hlLocked">
              <tbody #excelBody (mouseover)="onSheetRowHover($event)" (mouseleave)="onSheetRowHoverEnd()">
                <tr *ngFor="let row of sheet.grid; let ri = index">
                  <td *ngFor="let cell of row; let ci = index"
                      [attr.rowspan]="cell.rowspan ? cell.rowspan : null"
                      [attr.colspan]="cell.colspan ? cell.colspan : null"
                      [hidden]="cell.hidden"
                      [title]="cell.formula ? ('Formula: ' + cell.formula) : ''"
                      class="excel-cell"
                      [class.excel-cell--hl]="isHlCell(ri, ci)"
                      [class.excel-cell--hl-field]="isHlField(ri, ci)">
                    <div class="cell-content">
                      <ng-container *ngIf="showFormulas && cell.formula">
                        ={{cell.formula}}
                      </ng-container>
                      <ng-container *ngIf="!showFormulas">
                        <span *ngIf="showRaw && cell.raw !== null">{{cell.raw}}</span>
                        <span *ngIf="!showRaw">{{cell.text}}</span>
                      </ng-container>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="pdf-container-wrapper" *ngIf="!isLoading && isPdfFile">
      <div class="pdf-container highlight-host" #pdfHost>
        <!-- Use iframe for external PDFs to bypass CORS restrictions -->
        <iframe
          *ngIf="useIframe"
          [src]="data[0]"
          style="width: 100%; height: 100vh; border: none;"
          title="PDF Viewer"
        ></iframe>
        <pdf-viewer
          *ngIf="!useIframe"
          [src]="getUrlString(data[0])"
          [rotation]="0"
          [show-all]="true"
          [fit-to-page]="true"
          [zoom]="zoom"
          [zoom-scale]="'page-fit'"
          [page]="targetPage"
          (pageChange)="onPdfPageChange($event)"
          [external-link-target]="getUrlString(data[0])"
          [autoresize]="true"
          [show-borders]="false"
          [original-size]="false"
          [stick-to-page]="false"
          [render-text]="true"
          (pages-initialized)="onPdfPageEvent()"
          (page-rendered)="onPdfPageEvent()"
          (error)="onPdfViewerError()"
          style="width: 100%; height: 100vh;"
        ></pdf-viewer>
        <app-document-hotspot-layer
          *ngFor="let ph of pdfPageHotspots; trackBy: trackByPage"
          [regions]="ph.regions"
          [width]="ph.width"
          [height]="ph.height"
          [offsetLeft]="ph.left"
          [offsetTop]="ph.top"
          (hotspotHover)="onHotspotHover($event)"
        ></app-document-hotspot-layer>
        <app-document-highlight-layer
          *ngIf="!useIframe && activeBoxes.length && overlayWidth"
          [boxes]="activeBoxes"
          [width]="overlayWidth"
          [height]="overlayHeight"
          [offsetLeft]="overlayLeft"
          [offsetTop]="overlayTop"
        ></app-document-highlight-layer>
      </div>
    </div>

    <div *ngIf="!isLoading && isImageFile">
      <div class="image-container highlight-host" #imageHost>
        <img
          #previewImg
          [src]="data[0]"
          [alt]="'files_previewer.image_preview' | translate"
          class="preview-image"
          [style.transform]="'scale(' + zoom + ')'"
          (load)="onImageLoad()"
          (error)="onImageError($event)"
        />
        <app-document-hotspot-layer
          *ngIf="activeRegions.length && overlayWidth"
          [regions]="activeRegions"
          [width]="overlayWidth"
          [height]="overlayHeight"
          [offsetLeft]="overlayLeft"
          [offsetTop]="overlayTop"
          (hotspotHover)="onHotspotHover($event)"
        ></app-document-hotspot-layer>
        <app-document-highlight-layer
          *ngIf="activeBoxes.length && overlayWidth"
          [boxes]="activeBoxes"
          [width]="overlayWidth"
          [height]="overlayHeight"
          [offsetLeft]="overlayLeft"
          [offsetTop]="overlayTop"
        ></app-document-highlight-layer>
      </div>
    </div>

    <div *ngIf="!isLoading && isTextFile">
      <div *ngIf="textFallbackUrl" class="text-fallback">
        <a [href]="textFallbackUrl" target="_blank" rel="noopener noreferrer">
          {{ "files_previewer.open_in_new_tab" | translate }}
        </a>
      </div>
      <pre *ngIf="!textFallbackUrl" class="text-preview">{{ textContent }}</pre>
    </div>

    <div *ngIf="!isLoading && isHtmlFile">
      <iframe
        [src]="data[0]"
        sandbox=""
        class="html-preview"
        title="HTML Preview"
      ></iframe>
    </div>

    <div *ngIf="!isLoading && !isExcelOrCsvFile && !isPdfFile && !isImageFile && !isTextFile && !isHtmlFile && data && data.length > 0" class="unsupported-file">
      <div class="unsupported-message">
        <img src="assets/icones/attachment.svg" alt="File">
        <p>{{ "files_previewer.unsupported_file_type" | translate }}</p>
        <a
          *ngIf="unsupportedDownloadUrl"
          class="unsupported-download"
          [href]="unsupportedDownloadUrl"
          [attr.download]="unsupportedDownloadName"
        >{{ "files_previewer.download_file" | translate }}</a>
      </div>
    </div>
  </div>
</div>
